import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfo, IItemForGetShortDishInfo } from 'types';
import { Checkbox } from 'antd';
import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import { SuggestedDishCategorySelect } from './SuggestedDishCategorySelect';
import { SuggestedDishSubCategorySelect } from './SuggestedDishSubCategorySelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './MultipleSelect.module.scss';

interface IProps {
	refetch: () => void;
	setFetchedCategories: (value: IItemForGetShortInfo[]) => void;
	setFetchedSuggestedDishSubCategories: (value: IItemForGetShortInfo[]) => void;
	setSuggestedDishCategory: (value: Nullable<string>) => void;
	setSuggestedDishSubCategory: (value: Nullable<string>) => void;
	setSuggestedDishes: (value: string[]) => void;
	removeSuggestedDish: (value: string) => void;
	data: IItemForGetShortDishInfo[];
	IDsSelectedRestaurant: string[];
	selectedSuggestedDishCategory: Nullable<string>;
	selectedSuggestedDishSubCategory: Nullable<string>;
	fetchedSuggestedDishes: IItemForGetShortDishInfo[];
	selectedSuggestedDishes: string[];
	isLoading: boolean;
	responseError: boolean;
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({
		refetch,
		setFetchedCategories,
		setFetchedSuggestedDishSubCategories,
		setSuggestedDishCategory,
		setSuggestedDishSubCategory,
		setSuggestedDishes,
		removeSuggestedDish,
		data,
		IDsSelectedRestaurant,
		selectedSuggestedDishCategory,
		selectedSuggestedDishSubCategory,
		fetchedSuggestedDishes,
		selectedSuggestedDishes,
		isLoading,
		responseError,
	}) => {
		const [searchString, setSearchString] = useState<string>('');

		const filteredDataWithAdditionalContent = useMemo(() => {
			const updatedData = data.map((el) => ({
				...el,
				additionalContent: (
					<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
				),
			}));

			if (searchString) {
				const filteredDataByName = updatedData.filter((el) =>
					el.name.toLowerCase().includes(searchString.toLowerCase()),
				);
				const filteredDataByExternalId = updatedData.filter((el) =>
					el.externalId?.toLowerCase().includes(searchString.toLowerCase()),
				);
				const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
				return unionFilteredData;
			}
			return updatedData;
		}, [searchString, data]);

		const handleSelectAllData = useCallback(() => {
			const isCheckedAllItems = data.every((el) => selectedSuggestedDishes.includes(el.id));
			const dataIDs = data.map((el) => el.id);
			if (isCheckedAllItems) {
				setSuggestedDishes(selectedSuggestedDishes.filter((el) => !dataIDs.includes(el)));
			} else {
				const filteredData = data.map((el) => el.id).filter((el) => !selectedSuggestedDishes.includes(el));
				setSuggestedDishes([...selectedSuggestedDishes, ...filteredData]);
			}
		}, [data, selectedSuggestedDishes, setSuggestedDishes]);

		useEffect(() => {
			setSearchString('');
		}, [selectedSuggestedDishSubCategory]);

		const selectedItemsList = useMemo(() => {
			return selectedSuggestedDishes.map((el) => {
				const label = fetchedSuggestedDishes?.find((item) => item.id === el)?.name || '';

				return <SelectedItem key={el} label={label} onDelete={() => removeSuggestedDish(el)} />;
			});
		}, [selectedSuggestedDishes, fetchedSuggestedDishes, removeSuggestedDish]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const isChecked = () => {
					return data.every((el) => selectedSuggestedDishes.includes(el.id));
				};

				return (
					<div className={style.container}>
						<Text
							translationNameSpace="form"
							translationKey="fillInRequiredFilters"
							size="s"
							weight="bold"
						/>
						<div className={style.selectMenuHeader}>
							<div className={style.filterSelects}>
								<SuggestedDishCategorySelect
									IDsSelectedRestaurant={IDsSelectedRestaurant}
									setSuggestedDishCategory={setSuggestedDishCategory}
									setFetchedCategories={setFetchedCategories}
								/>
								<SuggestedDishSubCategorySelect
									selectedSuggestedDishCategory={selectedSuggestedDishCategory}
									setFetchedSuggestedDishSubCategories={setFetchedSuggestedDishSubCategories}
									setSuggestedDishSubCategory={setSuggestedDishSubCategory}
								/>
							</div>
							<SearchField onChangeSelectSearchString={setSearchString} searchString={searchString} />
							<Checkbox
								className={style.checkBox}
								checked={!!(data.length && isChecked())}
								disabled={!data.length}
								onChange={handleSelectAllData}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllDishesLabel"
									asFragment={true}
								/>
							</Checkbox>
							{!!selectedItemsList.length && (
								<div className={style.selectedItemsList}>{selectedItemsList}</div>
							)}
						</div>
						{menu}
					</div>
				);
			},
			[selectedSuggestedDishes, data, handleSelectAllData, setSearchString, searchString],
		);

		return (
			<FormSelect
				name="selectedSuggestedDishes"
				label="dishesLabel"
				options={filteredDataWithAdditionalContent}
				fetchError={responseError}
				loading={isLoading}
				disabled={!IDsSelectedRestaurant.length}
				onRefetch={refetch}
				onChange={setSuggestedDishes}
				counterValue={selectedSuggestedDishes.length}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
